import { Link } from 'react-router';
import type { ClientPackCheckSummary } from '@mcpanel/core';
import { api } from '../api/client';
import { usePoll } from '../hooks/usePoll';
import { LoadingContent, Spinner } from './feedback';
import { Empty, ErrorAlert } from './ui';
import { formatDateTime, formatGb } from '../format';

/** 同じ構成のサーバーは 1 回のテストにまとめて動くので、テストごとに並べる */
export function groupClientPackChecks(checks: ClientPackCheckSummary[]) {
  const groups = new Map<string, ClientPackCheckSummary[]>();
  for (const check of checks) {
    const key = check.test ?? check.serverId;
    groups.set(key, [...(groups.get(key) ?? []), check]);
  }
  return [...groups.values()];
}
/** 実行中のテストの数（VM でメモリを使っている数） */
export const runningClientPackTests = (checks: ClientPackCheckSummary[] | undefined) =>
  groupClientPackChecks(checks ?? []).filter((group) => group[0]!.state === 'running').length;

export function ClientPackChecks({ serverId }: { serverId?: string }) {
  const checks = usePoll(() => api.clientPackChecks(), 5000, []);
  const tests = groupClientPackChecks((checks.data ?? []).filter((check) => !serverId || check.serverId === serverId));
  const running = tests.filter((group) => group[0]!.state === 'running').length;
  const labels = { queued: '待機中', running: '実行中', succeeded: '成功', failed: '失敗' };
  return (
    <section className="card card-body stack" aria-label="クライアント起動テスト">
      <h2>
        {running > 0 && <Spinner />}
        クライアント起動テスト{running > 0 && `（実行中 ${running} 件）`}
      </h2>
      <p className="small muted">
        テストごとにテスト用の VM を作って動かすので、実行中はノードの CPU とメモリを使います。
      </p>
      <ErrorAlert error={checks.error} />
      <LoadingContent loading={!checks.data && checks.loading}>
        {tests.length === 0 ? (
          <Empty>この 24 時間に動いたテストはありません</Empty>
        ) : (
          tests.map((group) => {
            const check = group[0]!;
            return (
              <div className="stack" key={check.test ?? check.serverId}>
                <div className="row">
                  {group.map((entry, index) => (
                    <span key={entry.serverId}>
                      {index > 0 && '、'}
                      <Link to={`/servers/${encodeURIComponent(entry.serverId)}?tab=extensions&section=launcher`}>
                        {entry.serverName}
                      </Link>
                    </span>
                  ))}
                  <span>Minecraft {check.mcVersion}</span>
                  {check.templateName && <span className="small muted">テンプレート「{check.templateName}」</span>}
                  <span className="tag">{labels[check.state]}</span>
                  <span className="small">{formatDateTime(check.finishedAt ?? check.createdAt)}</span>
                </div>
                {check.vm && (
                  <p className="small">
                    テスト用 VM: {check.vm.node} / VM {check.vm.vmid} ・ メモリ {formatGb(check.vm.memoryMb)}
                  </p>
                )}
                {check.progress && (
                  <div className="meter" role="group" aria-label="クライアント起動テストの進み具合">
                    <div className="meter-head">
                      <span>
                        {check.progress.percent}% ・ {check.progress.label}
                      </span>
                    </div>
                    <div
                      className="meter-track"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={check.progress.percent}
                      aria-label={check.progress.label}
                    >
                      <div className="meter-fill" style={{ width: `${check.progress.percent}%` }} />
                    </div>
                  </div>
                )}
                {check.reason && !check.progress && <p className="small">{check.reason}</p>}
              </div>
            );
          })
        )}
      </LoadingContent>
    </section>
  );
}
